import { Link } from 'react-router-dom';
import { assignmentUi } from '../i18n/assignmentUi.js';
import { t } from '../i18n/translate.js';
import { formatDate } from '../utils/formatDate.js';
import { PRIORITIES } from '../utils/taskQueue.js';
import { statusLabel } from '../utils/statusUi.js';
import { StatusBadge } from './StatusBadge.jsx';

const COUNTS = [
  { id: 'overdue', label: assignmentUi.overdue, tone: 'danger' },
  { id: 'today', label: assignmentUi.dueToday, tone: 'warning' },
  { id: 'week', label: assignmentUi.thisWeek, tone: 'info' },
  { id: 'waiting', label: assignmentUi.waiting, tone: 'neutral' },
  { id: 'waiting_on_you', label: assignmentUi.waitingOnYou, tone: 'warning' },
];

const PREVIEW_LIMIT = 5;

function taskBadge(task, locale) {
  if (task.bucket === 'overdue') {
    return { label: t(assignmentUi.overdue, locale), tone: 'danger' };
  }
  if (task.waiting_on_me) {
    return { label: t(assignmentUi.waitingOnYou, locale), tone: 'warning' };
  }
  if (task.bucket === 'waiting' || task.in_waiting_stage || task.status === 'waiting') {
    return { label: t(assignmentUi.waiting, locale), tone: 'warning' };
  }
  return { label: statusLabel(task.status) };
}

function taskMeta(task, locale) {
  const context = [task.client_name, task.project_name].filter(Boolean).join(' — ');
  const priority = PRIORITIES.find((item) => item.id === task.priority);
  const priorityLabel = priority && task.priority !== 'medium' && task.priority !== 'low' ? priority.label : '';
  const waiting = task.waiting_on_me && task.bucket === 'overdue' ? t(assignmentUi.waitingOnYou, locale) : '';
  return [context, task.due_date ? formatDate(task.due_date) : '', priorityLabel, waiting].filter(Boolean).join(' · ');
}

export function MyWorkPanel({ work, locale }) {
  if (!work) return null;
  const counts = work.counts || {};
  const tasks = (work.tasks || []).slice(0, PREVIEW_LIMIT);

  return (
    <section className="panel my-work" aria-labelledby="my-work-title">
      <div className="panel-head">
        <h3 id="my-work-title">{t(assignmentUi.myWork, locale)}</h3>
        <Link className="panel-link" to="/admin/tasks?mine=1">{t(assignmentUi.viewAll, locale)}</Link>
      </div>
      <ul className="my-work-counts">
        {COUNTS.map((item) => {
          const value = Number(counts[item.id] || 0);
          return (
            <li key={item.id} className={`my-work-count my-work-count--${item.tone}${value ? '' : ' is-zero'}`}>
              <strong>{value}</strong>
              <span>{t(item.label, locale)}</span>
            </li>
          );
        })}
      </ul>
      {tasks.length ? (
        <ul className="my-work-list">
          {tasks.map((task) => {
            const meta = taskMeta(task, locale);
            const badge = taskBadge(task, locale);
            return (
              <li key={task.id}>
                <Link className="my-work-task" to={task.link || `/admin/tasks?mine=1&task=${task.id}`}>
                  <span className="my-work-task-copy">
                    <strong>{task.title}</strong>
                    {meta ? <span className="muted small">{meta}</span> : null}
                  </span>
                  <StatusBadge status={task.status} label={badge.label} tone={badge.tone} />
                </Link>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="my-work-empty">{t(assignmentUi.myWorkClear, locale)}</p>
      )}
    </section>
  );
}
